import React, { useEffect, useMemo, useRef, useState } from 'react';
import { School } from 'models/school';
import { getSchoolEvents, getSchoolWeekStatistics } from 'services/school';
import { getSchoolHousePointsTotalsCurrentYear } from 'services/awards';
import { getRecords } from 'services/getRecords';
import { getNews } from 'services/news';
import { getAllSchoolUnionTournaments } from 'services/schoolUnion/tournaments';
import { getEventGeneratedName } from 'helpers/event';
import { getEventResultForCalendar } from 'helpers/calendar';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getConvertedScore } from 'helpers/score';
import { getTournamentPublicSiteLink } from 'helpers/link';
import './SchoolWidget.scss';
import './SchoolWidgetExtra.scss';

type WidgetType = 'fixtures' | 'results' | 'club-events' | 'news' | 'records' | 'house-points' | 'weekly-stats' | 'tournament-upcoming' | 'tournament-results' | 'tournament-calendar' | 'live-today';
type WeekStatistics = { fixtures: number; clubs: number; fixturePlayers: number; clubPlayers: number; pupils: number };

const TYPES: WidgetType[] = ['fixtures', 'results', 'club-events', 'news', 'records', 'house-points', 'weekly-stats', 'tournament-upcoming', 'tournament-results', 'tournament-calendar', 'live-today'];
const EMPTY_STATS: WeekStatistics = { fixtures: 0, clubs: 0, fixturePlayers: 0, clubPlayers: 0, pupils: 0 };
const META: Record<WidgetType, { heading: string; href: string }> = {
  fixtures: { heading: 'Upcoming fixtures', href: '/fixtures' },
  results: { heading: 'Latest results', href: '/results' },
  'club-events': { heading: 'Upcoming clubs', href: '/clubEvents' },
  news: { heading: 'Latest news', href: '/news' },
  records: { heading: 'School records', href: '/records' },
  'house-points': { heading: 'House points', href: '/#house-points' },
  'weekly-stats': { heading: 'This week in sport', href: '/calendar' },
  'tournament-upcoming': { heading: 'Upcoming tournaments', href: '/upcomingTournaments' },
  'tournament-results': { heading: 'Latest tournament results', href: '/tournamentResults' },
  'tournament-calendar': { heading: 'Tournament calendar', href: '/calendar' },
  'live-today': { heading: 'Live today', href: '/calendar' },
};

const labelDate = (value: string) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(value));
const labelTime = (value: string) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
const eventTitle = (event: any, schoolId: string) => getEventGeneratedName(event, schoolId) || event.shortDescription || event.sport?.name || 'School fixture';
const eventResult = (event: any, schoolId: string) => {
  const value = String(getEventResultForCalendar(event, schoolId) || '');
  return /^view results$/i.test(value) ? 'See on website' : value.replace(/\s*:\s*/g, '–');
};
const plainText = (html = '') => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
const recordValue = (record: any) => {
  try { return getConvertedScore(record.record, record.sport) || String(record.record); }
  catch { return String(record.record || ''); }
};
const isTournamentWidget = (type: WidgetType) => ['tournament-upcoming', 'tournament-results', 'tournament-calendar', 'live-today'].includes(type);
const tournamentHref = (tournament: any, type: WidgetType) => {
  const shouldOpenTournamentSite = ['live-today', 'tournament-results'].includes(type) && tournament.domain && ['PUBLIC_AVAILABLE', 'PROTECTED'].includes(String(tournament.publicSite?.status));
  return shouldOpenTournamentSite ? getTournamentPublicSiteLink(tournament) : `/tournament/${tournament.id}`;
};
const hasTournamentSite = (tournament: any) => Boolean(tournament.domain) && ['PUBLIC_AVAILABLE', 'PROTECTED'].includes(String(tournament.publicSite?.status));

export const SchoolWidget = ({ school }: { school: School }) => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedType = params.get('type') as WidgetType;
  const type = TYPES.includes(requestedType) ? requestedType : 'fixtures';
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light';
  const limit = Math.max(1, Math.min(10, Number(params.get('limit')) || 5));
  const sport = params.get('sport') || '';
  const accent = /^#[0-9a-f]{6}$/i.test(params.get('accent') || '') ? params.get('accent')! : '#0878d1';
  const [items, setItems] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<WeekStatistics>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    let request: Promise<any>;
    if (isTournamentWidget(type)) {
      request = getAllSchoolUnionTournaments(school.id).then(tournaments => {
        const now = new Date();
        const today = now.toDateString();
        const rows = tournaments.filter(item => String(item.schoolId) === String(school.id) && (!sport || item.sportId === sport));
        if (type === 'live-today') return rows.filter(item => new Date(item.startTime).toDateString() === today).sort((a, b) => +new Date(a.startTime) - +new Date(b.startTime));
        if (type === 'tournament-results') return rows.filter(item => new Date(item.startTime) < now).sort((a, b) => +new Date(b.startTime) - +new Date(a.startTime));
        return rows.filter(item => new Date(item.startTime).setHours(23, 59, 59, 999) >= +now).sort((a, b) => +new Date(a.startTime) - +new Date(b.startTime));
      });
    } else if (type === 'house-points') {
      request = getSchoolHousePointsTotalsCurrentYear(school.id);
    } else if (type === 'weekly-stats') {
      request = getSchoolWeekStatistics(school.id);
    } else if (type === 'news') {
      request = getNews(school.id, { where: {}, skip: 0, limit, order: 'date DESC' });
    } else if (type === 'records') {
      request = getRecords(school.id);
    } else {
      const isResults = type === 'results';
      const where: any = type === 'club-events'
        ? { clubId: { $exists: true }, status: { $nin: ['CANCELED'] }, startTime: { $gte: new Date().toISOString() } }
        : isResults
          ? { clubId: { $exists: false }, status: 'FINISHED' }
          : { clubId: { $exists: false }, status: { $nin: ['CANCELED'] }, startTime: { $gte: new Date().toISOString() } };
      if (sport) where.sportId = sport;
      request = getSchoolEvents(school.id, { where, skip: 0, limit, order: isResults ? 'startTime DESC' : 'startTime ASC' });
    }
    request.then((data: any) => {
      if (type === 'weekly-stats') {
        setStatistics({ ...EMPTY_STATS, ...(data || {}) });
        setItems([]);
        return;
      }
      let rows = Array.isArray(data) ? data : [];
      if (type === 'house-points') rows = [...rows].sort((a, b) => Number(b.totalPoints) - Number(a.totalPoints));
      if (type === 'records') {
        rows = rows
          .filter(item => !sport || item.sportId === sport)
          .sort((a, b) => new Date(b.date || b.createdAt).getTime() - new Date(a.date || a.createdAt).getTime());
      }
      setItems(rows.slice(0, limit));
    }).catch(() => {
      setItems([]);
      setStatistics(EMPTY_STATS);
    }).finally(() => setLoading(false));
  }, [school.id, type, limit, sport]);

  useEffect(() => {
    if (!root.current) return;
    const send = () => window.parent.postMessage({ type: 'sit-widget-resize', height: root.current?.scrollHeight || 420 }, '*');
    send();
    const observer = new ResizeObserver(send);
    observer.observe(root.current);
    return () => observer.disconnect();
  }, [items, statistics, loading]);

  const meta = META[type];
  const content = (() => {
    if (loading) return <div className="sitWidgetState">Loading…</div>;
    if (type === 'weekly-stats') {
      const places = statistics.fixturePlayers + statistics.clubPlayers;
      return <div className="sitWidgetStats">
        {[[statistics.fixtures, 'Fixtures'], [statistics.clubs, 'Club sessions'], [statistics.pupils, 'Pupils involved'], [places, 'Total participations']].map(([value, label]) => <article key={String(label)}><strong>{value}</strong><span>{label}</span></article>)}
      </div>;
    }
    if (!items.length) return <div className="sitWidgetState">Nothing published yet.</div>;
    if (type === 'house-points') return <div className="sitWidgetHouses">{items.map((item, index) => <article key={item.houseId || item.id}><em>{index + 1}</em><strong>{item.houseName || item.name}</strong><span>{item.totalPoints} pts</span></article>)}</div>;
    if (type === 'news') return <div className="sitWidgetNews">{items.map(item => <article key={item.id}><time>{labelDate(item.date)}</time><div><strong>{item.title}</strong>{item.body && <p>{plainText(item.body)}</p>}</div></article>)}</div>;
    if (type === 'records') return <div className="sitWidgetRecords">{items.map(item => <article key={item.id}><div><small>{item.sport?.name || 'School sport'}{item.ages?.length ? ` · ${getAgesGroupFormatted(item, school)}` : ''}</small><strong>{item.athlete || 'School record'}</strong></div><em>{recordValue(item)}</em></article>)}</div>;
    if (isTournamentWidget(type)) return <div className="sitWidgetEvents sitWidgetTournaments">{items.map(tournament => <a href={tournamentHref(tournament, type)} target="_blank" rel="noreferrer" key={tournament.id}><time><b>{labelDate(tournament.startTime)}</b><span>{labelTime(tournament.startTime)}</span></time><div><small>{tournament.sport?.name || 'Tournament'}{tournament.ages?.length ? ` · ${getAgesGroupFormatted(tournament, school)}` : ''}</small><strong>{tournament.name}</strong></div><i>{type === 'live-today' && hasTournamentSite(tournament) ? 'Live' : type === 'tournament-results' && hasTournamentSite(tournament) ? 'Results' : 'Details'}</i></a>)}</div>;
    return <div className="sitWidgetEvents">{items.map(event => <article key={event.id}><time><b>{labelDate(event.startTime)}</b><span>{labelTime(event.startTime)}</span></time><div><small>{event.sport?.name || (type === 'club-events' ? 'School club' : 'School sport')}</small><strong>{eventTitle(event, school.id)}</strong></div>{type === 'results' ? <em>{eventResult(event, school.id) || 'See on website'}</em> : <i>{event.isHomeEvent ? 'Home' : type === 'club-events' ? 'Club' : 'Away'}</i>}</article>)}</div>;
  })();

  return <div ref={root} className="sitWidget" data-theme={theme} style={{ ['--widget-accent' as any]: accent }}>
    <header><div>{school.pic && <img src={school.pic} alt="" />}<span><b>{meta.heading}</b><small>{school.name}</small></span></div><a href={meta.href} target="_blank" rel="noreferrer">View all <span>→</span></a></header>
    {content}
    <footer>Powered by <b>Squad In Touch</b></footer>
  </div>;
};
